import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

import {
    fetchRefundPreview,
    submitRefund,
    type RefundPreview,
} from '~/services/refund';
import { formatCurrency } from '~/common/form/Inputs';

type Props = {
    isOpen: boolean;
    lookupId: string | null;
    donorName: string;
    onClose: () => void;
};

type RegistrationDetail = NonNullable<RefundPreview['registration']>['registration_details'][number];

export default function RefundModal({
    isOpen,
    lookupId,
    donorName,
    onClose,
}: Props) {
    const queryClient = useQueryClient();
    const [donationAmount, setDonationAmount] = useState<string>('');
    const [refundTip, setRefundTip] = useState<boolean>(false);
    const [tipRefundAmount, setTipRefundAmount] = useState<string>('');
    const [itemQuantities, setItemQuantities] = useState<Record<number, number>>({});
    const [cancelNote, setCancelNote] = useState<string>('');
    const [confirmStage, setConfirmStage] = useState<boolean>(false);

    const previewQuery = useQuery<RefundPreview>({
        queryKey: ['refund-preview', lookupId],
        queryFn: () => fetchRefundPreview(lookupId!),
        enabled: isOpen && !!lookupId,
        staleTime: 0,
    });

    useEffect(() => {
        if (previewQuery.data) {
            setDonationAmount(previewQuery.data.donation_amount);
            setRefundTip(false);
            setTipRefundAmount(previewQuery.data.tip?.amount ?? '');
            setCancelNote('');
            const initial: Record<number, number> = {};
            previewQuery.data.registration?.registration_details.forEach((d) => {
                initial[d.id] = 0;
            });
            setItemQuantities(initial);
            setConfirmStage(false);
        }
    }, [previewQuery.data]);

    const isEvent = !!previewQuery.data?.registration;
    const tipAmount = Number(previewQuery.data?.tip?.amount || 0);

    const computedDonationAmount = useMemo(() => {
        if (!isEvent || !previewQuery.data?.registration) return Number(donationAmount || 0);
        return previewQuery.data.registration.registration_details.reduce(
            (sum, d) => sum + Number(d.price_paid) * (itemQuantities[d.id] || 0),
            0
        );
    }, [isEvent, itemQuantities, previewQuery.data, donationAmount]);

    useEffect(() => {
        if (isEvent) {
            setDonationAmount(computedDonationAmount.toFixed(2));
        }
    }, [computedDonationAmount, isEvent]);

    // The tip's share of the part of the donation being refunded. Refunding one
    // registration out of a multi-registration order should give back only that
    // registration's slice of the tip (HHLTB-2784); refunding everything that is
    // left works out to the whole tip.
    const suggestedTipAmount = useMemo(() => {
        const donationTotal = Number(previewQuery.data?.donation_amount || 0);
        if (!tipAmount || donationTotal <= 0) return tipAmount;
        const share = Math.min(computedDonationAmount / donationTotal, 1);
        return Math.round(tipAmount * share * 100) / 100;
    }, [tipAmount, computedDonationAmount, previewQuery.data]);

    // Follow the line items while the amount is untouched, so the common case
    // needs no typing; a hand-edited amount stays put.
    const [tipAmountEdited, setTipAmountEdited] = useState<boolean>(false);
    useEffect(() => {
        if (!tipAmountEdited) {
            setTipRefundAmount(suggestedTipAmount.toFixed(2));
        }
    }, [suggestedTipAmount, tipAmountEdited]);

    useEffect(() => {
        if (previewQuery.data) setTipAmountEdited(false);
    }, [previewQuery.data]);

    const tipRefundValue = refundTip ? Number(tipRefundAmount || 0) : 0;
    const tipRefundInvalid =
        refundTip && (tipRefundValue <= 0 || tipRefundValue > tipAmount);

    const total = computedDonationAmount + tipRefundValue;

    // Show cancel_note textarea when the refund would remove the full quantity
    // of every remaining line item (event case)
    const wouldCancelRegistration = useMemo(() => {
        if (!isEvent || !previewQuery.data?.registration) return false;
        const details = previewQuery.data.registration.registration_details;
        if (details.length === 0) return false;
        return details.every((d) => (itemQuantities[d.id] || 0) === d.quantity);
    }, [isEvent, itemQuantities, previewQuery.data]);

    const mutation = useMutation({
        mutationFn: () =>
            submitRefund({
                lookup_id: lookupId!,
                donation_refund_amount: donationAmount,
                refund_tip: refundTip,
                tip_refund_amount: refundTip ? tipRefundAmount : undefined,
                registration_items: Object.entries(itemQuantities)
                    .filter(([, qty]) => qty > 0)
                    .map(([id, qty]) => ({
                        registration_detail_id: Number(id),
                        refund_quantity: qty,
                    })),
                cancel_note: cancelNote,
            }),
        onSuccess: () => {
            toast.success('Refund processed');
            queryClient.invalidateQueries({ queryKey: ['all-supporters'] });
            queryClient.invalidateQueries({ queryKey: ['all-refunds'] });
            queryClient.invalidateQueries({ queryKey: ['campaign-refunds'] });
            queryClient.invalidateQueries({ queryKey: ['refund-history', lookupId] });
            onClose();
        },
        onError: (err: Error) => {
            toast.error(err.message);
            setConfirmStage(false);
        },
    });

    if (!isOpen || !lookupId) return null;

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
            <div className='max-h-[90vh] w-[540px] overflow-y-auto rounded bg-white p-[24px]'>
                <h2 className='mb-[16px] text-[20px] font-bold'>
                    Refund Donation — {donorName}
                </h2>

                {previewQuery.isLoading && <div>Loading…</div>}
                {previewQuery.isError && (
                    <div className='text-red-500'>
                        {(previewQuery.error as Error).message}
                    </div>
                )}

                {previewQuery.data && (
                    <>
                        {isEvent ? (
                            <EventLineItemsSection
                                details={previewQuery.data.registration!.registration_details}
                                quantities={itemQuantities}
                                onChange={setItemQuantities}
                            />
                        ) : (
                            <div className='mb-[16px]'>
                                <label className='block text-[12px] uppercase text-[#949599]'>
                                    Donation refund amount
                                </label>
                                <input
                                    type='number'
                                    step='0.01'
                                    min='0'
                                    max={previewQuery.data.donation_amount}
                                    value={donationAmount}
                                    onChange={(e) => setDonationAmount(e.target.value)}
                                    className='mt-1 w-full rounded border border-stroke p-2'
                                />
                                <div className='mt-1 text-xs text-[#949599]'>
                                    Original donation: {formatCurrency(Number(previewQuery.data.donation_amount))}
                                </div>
                            </div>
                        )}

                        {previewQuery.data.tip && (
                            <div className='mb-[16px]'>
                                <label className='flex items-center gap-2'>
                                    <input
                                        type='checkbox'
                                        checked={refundTip}
                                        onChange={(e) => setRefundTip(e.target.checked)}
                                    />
                                    <span>
                                        Also refund tip (tip paid:{' '}
                                        {formatCurrency(tipAmount)})
                                    </span>
                                </label>
                                {refundTip && (
                                    <div className='mt-2 pl-6'>
                                        <label className='block text-[12px] uppercase text-[#949599]'>
                                            Tip refund amount
                                        </label>
                                        <input
                                            type='number'
                                            step='0.01'
                                            min='0'
                                            max={tipAmount}
                                            value={tipRefundAmount}
                                            onChange={(e) => {
                                                setTipAmountEdited(true);
                                                setTipRefundAmount(e.target.value);
                                            }}
                                            className='mt-1 w-40 rounded border border-stroke p-2'
                                        />
                                        {tipRefundInvalid ? (
                                            <div className='mt-1 text-xs text-red-500'>
                                                Enter a tip amount between $0.01 and{' '}
                                                {formatCurrency(tipAmount)}.
                                            </div>
                                        ) : (
                                            <div className='mt-1 text-xs text-[#949599]'>
                                                Proportional to the amount being
                                                refunded ({formatCurrency(suggestedTipAmount)}).
                                                Edit to refund a different amount.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {wouldCancelRegistration && (
                            <div className='mb-[16px]'>
                                <label className='block text-[12px] uppercase text-[#949599]'>
                                    Cancelation note (optional)
                                </label>
                                <textarea
                                    value={cancelNote}
                                    onChange={(e) => setCancelNote(e.target.value)}
                                    rows={3}
                                    className='mt-1 w-full rounded border border-stroke p-2'
                                />
                            </div>
                        )}

                        <div className='mb-[16px] rounded bg-[#e4f6f7] p-3 text-[14px]'>
                            Refunding {formatCurrency(total)} (Donation{' '}
                            {formatCurrency(computedDonationAmount)} + Tip{' '}
                            {formatCurrency(tipRefundValue)})
                        </div>

                        {confirmStage && (
                            <div className='mb-[12px] rounded border border-red-300 bg-red-50 p-3 text-[13px] text-red-800'>
                                Are you sure? This cannot be undone.
                            </div>
                        )}

                        <div className='flex justify-end gap-3'>
                            <button
                                type='button'
                                onClick={onClose}
                                className='rounded-full border-[2px] border-stroke px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                            >
                                Cancel
                            </button>
                            {!confirmStage ? (
                                <button
                                    type='button'
                                    disabled={total <= 0 || tipRefundInvalid}
                                    onClick={() => setConfirmStage(true)}
                                    className='rounded-full border-[2px] border-black bg-black px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:bg-white hover:text-black disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-black disabled:hover:text-white'
                                >
                                    Continue
                                </button>
                            ) : (
                                <button
                                    type='button'
                                    disabled={mutation.isPending}
                                    onClick={() => mutation.mutate()}
                                    style={{ backgroundColor: '#b91c1c', borderColor: '#b91c1c' }}
                                    className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-opacity duration-300 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
                                >
                                    {mutation.isPending ? 'Refunding…' : 'Confirm refund'}
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

type LineItemsProps = {
    details: RegistrationDetail[];
    quantities: Record<number, number>;
    onChange: (q: Record<number, number>) => void;
};

function EventLineItemsSection({ details, quantities, onChange }: LineItemsProps) {
    return (
        <div className='mb-[16px]'>
            <div className='mb-2 text-[12px] uppercase text-[#949599]'>
                Registration items
            </div>
            <table className='w-full text-[14px]'>
                <thead>
                    <tr className='text-left text-[12px] text-[#949599]'>
                        <th>Item</th>
                        <th>Price</th>
                        <th>Qty</th>
                        <th>Refund qty</th>
                    </tr>
                </thead>
                <tbody>
                    {details.map((d) => {
                        const name =
                            d.registration_option?.name ||
                            d.registration_addon?.name ||
                            '—';
                        return (
                            <tr key={d.id}>
                                <td>
                                    {name}
                                    {d.option_question_answer ? ` — ${d.option_question_answer}` : ''}
                                    {d.addon_option_selection ? ` (${d.addon_option_selection})` : ''}
                                </td>
                                <td>{formatCurrency(Number(d.price_paid))}</td>
                                <td>{d.quantity}</td>
                                <td>
                                    <input
                                        type='number'
                                        min={0}
                                        max={d.quantity}
                                        value={quantities[d.id] ?? 0}
                                        onChange={(e) =>
                                            onChange({
                                                ...quantities,
                                                [d.id]: Math.max(
                                                    0,
                                                    Math.min(
                                                        d.quantity,
                                                        Number(e.target.value) || 0
                                                    )
                                                ),
                                            })
                                        }
                                        className='w-20 rounded border border-stroke p-1'
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
